import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api.ts';
import { useItUsers } from '../lib/it.ts';
import { sessionKey, useSession } from '../lib/session.ts';
import type { User } from '../lib/types.ts';
import { Button } from './ui/button.tsx';
import { Dialog } from './ui/dialog.tsx';
import { ChoiceCards, Field, Input, Select } from './ui/form.tsx';
import { Alert } from './ui/misc.tsx';
import { useToast } from './ui/toast.tsx';

type Choice = 'in' | 'duty' | 'off';
const today = () => new Date().toISOString().slice(0, 10);
const hhmm = (d: Date) => d.toTimeString().slice(0, 5);

/** For the account menu: "In the office", "Out on official duty until 15:00" or "Off today". */
export function statusText(user: User): { text: string; away: boolean } {
  if (user.away && user.away.from <= today() && (user.away.until ?? '') >= today()) {
    return { text: user.away.reason === 'off' ? 'Off today' : 'On leave', away: true };
  }
  if (user.outUntil && new Date(user.outUntil) > new Date()) {
    return { text: `Out on duty until ${hhmm(new Date(user.outUntil))}`, away: true };
  }
  return { text: 'In the office', away: false };
}

/**
 * Quick status for IT: out of the premises on official duty (tickets still
 * come to you, staff see when you're back), or off today at short notice
 * (illness, an emergency: a colleague can cover, and staff aren't told why).
 */
export function StatusDialog({ onClose }: { onClose: () => void }) {
  const { user } = useSession();
  const client = useQueryClient();
  const toast = useToast();
  // Shares the cache with the assignment picker (lib/it.ts).
  const itUsers = useItUsers();
  const colleagues = { data: itUsers.data?.filter((u) => u.id !== user?.id) };
  const current = user ? statusText(user) : null;
  const [choice, setChoice] = useState<Choice>(
    current?.text === 'Off today' ? 'off' : current?.text.startsWith('Out on duty') ? 'duty' : 'in',
  );
  const [until, setUntil] = useState(() => {
    const d = new Date(Date.now() + 2 * 3600_000);
    d.setMinutes(0);
    return hhmm(d);
  });
  const [cover, setCover] = useState(user?.away?.coverUserId ? String(user.away.coverUserId) : '');
  const [error, setError] = useState<string>();

  const save = useMutation({
    mutationFn: async () => {
      const offToday = user?.away?.reason === 'off';
      if (choice === 'duty') {
        const [h, m] = until.split(':').map(Number);
        const back = new Date();
        back.setHours(h!, m!, 0, 0);
        await api.put('/api/me/out', { until: back.toISOString() });
        if (offToday) await api.put('/api/me/away', null);
      } else if (choice === 'off') {
        await api.put('/api/me/away', {
          from: today(),
          until: today(),
          coverUserId: cover ? Number(cover) : null,
          reason: 'off',
        });
        await api.put('/api/me/out', { until: null });
      } else {
        await api.put('/api/me/out', { until: null });
        if (offToday) await api.put('/api/me/away', null);
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: sessionKey });
      void client.invalidateQueries({ queryKey: ['office-status'] });
      toast({ tone: 'success', title: 'Status updated', body: 'Staff see it on the helpdesk.' });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="My status"
      description="Staff see where IT is, so they know what to expect."
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        {error && <Alert tone="error">{error}</Alert>}
        <ChoiceCards
          name="status"
          label="Where are you?"
          value={choice}
          onChange={(v) => setChoice(v as Choice)}
          options={[
            { value: 'in', label: 'In the office', description: 'Available as usual.' },
            {
              value: 'duty',
              label: 'Out on official duty',
              description: 'Out of the premises for work. Tickets still come to you.',
            },
            {
              value: 'off',
              label: 'Off today',
              description: 'Illness or an emergency. Staff just see “out of the office today”.',
            },
          ]}
        />
        {choice === 'duty' && (
          <Field label="Back by (today)">
            <Input
              type="time"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="max-w-40"
            />
          </Field>
        )}
        {choice === 'off' && (
          <Field label="Who covers today" hint="New tickets go to them; you still get copies.">
            <Select
              value={cover}
              onChange={(e) => setCover(e.target.value)}
              placeholder="Nobody (remote only)"
              options={(colleagues.data ?? []).map((u) => ({
                value: String(u.id),
                label: u.name || u.email,
              }))}
            />
          </Field>
        )}
        <p className="text-sm text-muted">
          For leave over several days, use Leave and cover in Profile &amp; notifications.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
